import React from "react";
import { motion } from "framer-motion";
import { BookOpenText, Target, Mic2, Brain } from "lucide-react";

const PILLARS = [
  { icon: Mic2, label: "Your voice", hint: "Tone, pace, pronunciation" },
  { icon: Brain, label: "Your patterns", hint: "Grammar you use vs. avoid" },
  { icon: BookOpenText, label: "Your gaps", hint: "Words you almost said" },
  { icon: Target, label: "Your goal", hint: "Where you want to be" },
];

const MemorySystem = () => {
  return (
    <section id="memory" className="relative py-28 px-6" data-testid="section-memory-system">
      <div className="max-w-7xl mx-auto">
        <div className="section-label">The memory system</div>
        <h2 className="headline mt-4 text-4xl sm:text-5xl lg:text-[88px] max-w-4xl">
          Four things AURA <em>remembers</em>.
        </h2>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((P, i) => (
            <motion.div
              key={P.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="glass rounded-3xl p-7 relative overflow-hidden group min-h-[220px]"
              data-testid={`memory-pillar-${P.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-2xl bg-aura-cyan/10 border border-aura-cyan/25 grid place-items-center text-aura-cyan">
                  <P.icon size={18} strokeWidth={1.5} />
                </div>
                <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-7 font-display text-[24px] font-light text-ink tracking-tight">{P.label}</h3>
              <p className="mt-2 text-whisper text-[14px] leading-[1.55]">{P.hint}</p>
              <div className="mt-6 h-[2px] w-full bg-panel/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-aura-cyan via-aura-blue to-aura-violet rounded-full transition-all duration-700 group-hover:w-full"
                  style={{ width: `${35 + i * 12}%` }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary row */}
        <div className="mt-6 glass rounded-3xl p-8 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-lg">
            <div className="font-serif italic text-[26px] leading-[1.2] text-ink">
              "Every conversation becomes a memory. Every memory rewires the next lesson."
            </div>
          </div>
          <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
            / persistent cognitive map
          </div>
        </div>
      </div>
    </section>
  );
};

export default MemorySystem;
